import classNames from 'classnames/bind';
import styles from './EbookActivityInfo.module.css';
import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import { selectUserAlias } from '../../../features/authSlice';
import {
  ALL,
  ALL_GRADE_CLASSES,
  ALL_SCHOOL_GRADES,
  EBOOK_ACTIVITY_SUFFIX,
  EMPTY_EBOOK_LIST,
  EMPTY_STUDENT_LIST,
  GRADE,
  GROUP,
  LAST_LOGIN_DATE,
  NAME,
  PROJECT_STUDENT_ACTIVITIES,
  SCHOOL_NAME,
  SEARCH_RESULT_NOT_FOUND,
  TOTAL_STUDY_TIME,
} from '../../../constants';
import Header from '../../../components/Dashboard/Header';
import {
  ActivityProjectList,
  ClassOptions,
  Student,
  StudentOptions,
} from '../../../types/analysis';
import {
  analysisApiSlice,
  useGetClassInitQuery,
  useGetStudentSummaryMutation,
} from '../../../services/analysis';
import { useEffect, useRef, useState } from 'react';
import {
  groupProjectIdChanged,
  groupProjectListChanged,
  projectStudentListChanged,
  selectGroupOptions,
  selectGroupProjectId,
  studentUserIdChanged,
} from '../../../features/analysisSlice';
import Search from '../../../components/Search';
import Slider from '../../../components/Slider';
import Filter from '../../../components/Filter';
import Table from '../../../components/Table';
import { isMobile } from '../../../utils/util';
import { useNavigate } from 'react-router-dom';

export default function EbookActivityInfo() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const alias = useAppSelector(selectUserAlias) ?? '';
  const options = useAppSelector(selectGroupOptions);
  const projectId = useAppSelector(selectGroupProjectId);

  const [studentOptions, setStudentOptions] = useState<StudentOptions>({
    schoolType: [],
    gradeType: [],
    search: '',
  });
  const [selectedGroup, setSelectedGroup] = useState(ALL);
  const [projectList, setProjectList] = useState<ActivityProjectList>([]);
  const [projectSearchText, setProjectSearchText] = useState('');
  const [studentList, setStudentList] = useState<Student[]>([]);
  const [selectDefault, setSelectDefault] = useState({
    key: 'totalTime',
    value: TOTAL_STUDY_TIME,
  });

  const [getSummary] = useGetStudentSummaryMutation();
  const { data: initialValues, isLoading: isLoadingInit } =
    useGetClassInitQuery();

  const groupList = initialValues?.groupList || [];
  const { schoolTypeList = [], gradeTypeList = [] } =
    initialValues?.commonType || {};
  const [getStudentProject] = analysisApiSlice.useLazyGetStudentProjectQuery();

  const studentListSectionRef = useRef<HTMLDivElement | null>(null);
  const dashSectionRef = useRef<HTMLDivElement | null>(null);

  const changeSummary = async (options: ClassOptions) => {
    try {
      const res = await getSummary(options);
      if ('data' in res) {
        if (res.data) {
          const { projectList } = res.data;
          setProjectList(projectList);
          dispatch(groupProjectListChanged(projectList));
        } else {
          setProjectList([]);
        }
      }
    } catch (error) {
      console.error('Fail to get Summary', error);
    }
  };

  const selectTableHeader = (value: string) => {
    const entries = Object.entries(PROJECT_STUDENT_ACTIVITIES);
    let key = '';

    for (const [k, val] of entries) {
      if (val === value) {
        key = k;
      }
    }
    setSelectDefault({ key, value });
  };

  const submitProjectSearch = (keyword: string) => {
    setProjectSearchText(keyword);
    dispatch(groupProjectIdChanged(0));
  };

  const handleGroupChange = (value: string) => {
    setSelectedGroup(value);
  };

  const submitStudentSearchKeyword = (keyword: string) => {
    setStudentOptions({ ...studentOptions, search: keyword });
  };

  const getStudentList = async () => {
    try {
      const { data } = await getStudentProject({
        projectId,
        data: studentOptions,
      });
      setStudentList(data.studentList);
      dispatch(projectStudentListChanged(data.studentList));
    } catch (error) {
      console.error('Fail to get Student list', error);
    }
  };

  useEffect(() => {
    dispatch(studentUserIdChanged(0));
    dispatch(groupProjectIdChanged(0));
  }, []);

  useEffect(() => {
    if (!!projectId) {
      getStudentList();
    }
  }, [projectId, studentOptions]);

  useEffect(() => {
    if (options) {
      const group = groupList.find((group) => group.groupNm === selectedGroup);
      const seq = group ? [group.seq] : [];

      changeSummary({ ...options, search: projectSearchText, seq });
      dispatch(groupProjectIdChanged(0));
    }
  }, [options, projectSearchText, selectedGroup]);

  if (isLoadingInit || !initialValues || !options || !projectList) return null;

  return (
    <>
      <div className="ebook_activity_info">
        <Header
          title={EBOOK_ACTIVITY_SUFFIX.replace('{user}', `${alias}`).replace(
            '{ebook}',
            ''
          )}
          groupList={groupList}
          selectedGroup={selectedGroup}
          onGroupChange={handleGroupChange}
        >
          <Search onSubmit={submitProjectSearch} />
        </Header>
      </div>

      <div className={cx('dash_section')} ref={dashSectionRef}>
        <div className={cx('dash_book_list')}>
          {projectList.length ? (
            <Slider
              name="activity-ebook-list"
              list={projectList}
              dashSectionRef={dashSectionRef}
            />
          ) : (
            <div className={cx('empty_upload')}>
              <p className={cx('desc')}>
                {projectSearchText ? SEARCH_RESULT_NOT_FOUND : EMPTY_EBOOK_LIST}
              </p>
            </div>
          )}
        </div>
      </div>

      {!!projectId && (
        <div className={cx('dash_section')} ref={studentListSectionRef}>
          <div className={cx('dash_class_data')}>
            <div className={cx('dash_option')}>
              <Search
                name={'activity-student'}
                onSubmit={submitStudentSearchKeyword}
                isBgWhite={true}
              />
              <Filter
                list={[
                  {
                    name: 'schoolType',
                    values: [
                      ALL_SCHOOL_GRADES,
                      ...schoolTypeList.map(({ nm }) => nm),
                    ],
                  },
                  {
                    name: 'gradeType',
                    values: [
                      ALL_GRADE_CLASSES,
                      ...gradeTypeList.map(({ nm }) => nm),
                    ],
                  },
                ]}
                onCheck={(checkedList) => {
                  const schoolType = checkedList.schoolType?.includes(
                    ALL_SCHOOL_GRADES
                  )
                    ? []
                    : schoolTypeList
                        .filter(({ nm }) =>
                          checkedList.schoolType?.includes(nm)
                        )
                        .map(({ cd }) => cd);
                  const gradeType = checkedList.gradeType?.includes(
                    ALL_GRADE_CLASSES
                  )
                    ? []
                    : gradeTypeList
                        .filter(({ nm }) => checkedList.gradeType?.includes(nm))
                        .map(({ cd }) => cd);

                  setStudentOptions({
                    ...studentOptions,
                    schoolType,
                    gradeType,
                  });
                }}
              />
            </div>

            {studentList.length ? (
              <Table
                name="activity-students"
                headList={
                  isMobile
                    ? [NAME, GROUP, TOTAL_STUDY_TIME]
                    : Object.values(PROJECT_STUDENT_ACTIVITIES)
                }
                bodyList={studentList.map((data) => ({
                  ...data,
                  id: data.userId,
                }))}
                keyList={
                  isMobile
                    ? ['nm', 'groupNames', 'totalTime']
                    : Object.keys(PROJECT_STUDENT_ACTIVITIES)
                }
                {...(isMobile && {
                  selectOptions: {
                    idx: 2,
                    default: {
                      key: selectDefault.key,
                      value: selectDefault.value,
                    },
                    values: [
                      TOTAL_STUDY_TIME,
                      SCHOOL_NAME,
                      GRADE,
                      LAST_LOGIN_DATE,
                    ],
                    onClick: selectTableHeader,
                  },
                })}
                onClickBody={({ id }) =>
                  navigate('../student-activity', {
                    state: { projectId, userId: id },
                  })
                }
              />
            ) : (
              <div className={cx('empty_upload')}>
                <p className={cx('desc')}>
                  {studentOptions.search
                    ? SEARCH_RESULT_NOT_FOUND
                    : EMPTY_STUDENT_LIST}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
